<!-- Product Group Edit/Create Page - Two-Column Master-Detail Layout -->
<p-toast />

<div class="product-group-edit-page">
  <!-- Loading Skeleton -->
  @if (loading()) {
    <div class="loading-skeleton">
      <div class="skeleton-header">
        <p-skeleton width="2rem" height="2rem" shape="circle" />
        <p-skeleton width="200px" height="1.5rem" />
      </div>
      <div class="skeleton-content">
        <div class="skeleton-left">
          <p-skeleton height="3rem" styleClass="mb-3" />
          <p-skeleton height="2rem" styleClass="mb-2" />
          <p-skeleton height="1rem" width="60%" />
        </div>
        <div class="skeleton-right">
          <p-skeleton height="3rem" styleClass="mb-3" />
          <p-skeleton height="8rem" />
        </div>
      </div>
    </div>
  } @else {
    <!-- Header with Back Button -->
    <div class="page-header">
      <div class="header-left">
        <p-button
          icon="pi pi-arrow-left"
          [text]="true"
          [rounded]="true"
          severity="secondary"
          (onClick)="goBack()"
          pTooltip="Back to Product Groups"
        />
        <h1 class="page-title">{{ pageTitle }}</h1>
        @if (mode === "edit" && productGroupId) {
          <span class="id-badge">#{{ productGroupId }}</span>
        }
      </div>
      <div class="header-right">
        <p-button
          [label]="submitButtonLabel"
          icon="pi pi-save"
          size="small"
          [loading]="saving()"
          [disabled]="saving() || productGroupForm.invalid"
          (onClick)="onSubmit()"
        />
      </div>
    </div>

    <!-- Form -->
    <form
      [formGroup]="productGroupForm"
      (ngSubmit)="onSubmit()"
      class="edit-form"
    >
      <!-- Desktop: Two-Column Layout -->
      <div class="desktop-layout">
        <div class="two-column-layout">
          <!-- Left Column: Group Details -->
          <div class="left-column">
            <p-card>
              <ng-template pTemplate="header">
                <div class="card-header">
                  <h2 class="card-title">Group Details</h2>
                </div>
              </ng-template>
              <ng-container *ngTemplateOutlet="groupDetailsContent" />
            </p-card>
          </div>

          <!-- Right Column: Products Panel -->
          <div class="right-column">
            <p-card styleClass="products-card">
              <ng-template pTemplate="header">
                <div class="card-header">
                  <h2 class="card-title">
                    Products in Group
                    @if (products().length > 0) {
                      <span class="count-badge">({{ products().length }})</span>
                    }
                  </h2>
                </div>
              </ng-template>
              <ng-container *ngTemplateOutlet="productsContent" />
            </p-card>
          </div>
        </div>
      </div>

      <!-- Mobile: Accordion Layout -->
      <div class="mobile-layout">
        <p-accordion [multiple]="true" [value]="['0', '1']">
          <p-accordion-panel value="0">
            <p-accordion-header>Group Details</p-accordion-header>
            <p-accordion-content>
              <ng-container *ngTemplateOutlet="groupDetailsContent" />
            </p-accordion-content>
          </p-accordion-panel>
          <p-accordion-panel value="1">
            <p-accordion-header>
              Products in Group
              @if (products().length > 0) {
                <span class="count-badge">({{ products().length }})</span>
              }
            </p-accordion-header>
            <p-accordion-content>
              <ng-container *ngTemplateOutlet="productsContent" />
            </p-accordion-content>
          </p-accordion-panel>
        </p-accordion>
      </div>

      <!-- Shared Templates (must be inside form for formControlName to work) -->
      <ng-template #groupDetailsContent>
        <div class="card-content">
          <!-- Name Field -->
          <div class="form-field">
            <input
              pInputText
              id="name"
              formControlName="name"
              placeholder="Name *"
              class="w-full"
              [class.ng-invalid]="hasError('name')"
            />
            <small class="field-hint" [class.error-hint]="hasError('name')">
              @if (hasError("name")) {
                {{ getErrorMessage("name") }}
              } @else {
                Required. Maximum 255 characters.
              }
            </small>
          </div>

          <!-- Summary Section (Edit Mode Only) -->
          @if (mode === "edit" && originalProductGroup) {
            <div class="summary-section">
              <h4 class="summary-title">Group Info</h4>
              <div class="summary-row">
                <span class="summary-label">Products</span>
                <span class="summary-value">{{ totalProductsCount }}</span>
              </div>
              <div class="summary-row">
                <span class="summary-label">Group ID</span>
                <span class="summary-value"
                  >#{{ originalProductGroup.ProductGroupId }}</span
                >
              </div>
              <div class="summary-row">
                <span class="summary-label">Created</span>
                <span class="summary-value">{{
                  originalProductGroup.CreatedUtc | date: "MMM d, yyyy"
                }}</span>
              </div>
              @if (originalProductGroup.UpdatedUtc) {
                <div class="summary-row">
                  <span class="summary-label">Updated</span>
                  <span class="summary-value">{{
                    originalProductGroup.UpdatedUtc | date: "MMM d, yyyy"
                  }}</span>
                </div>
              }
            </div>
          }
        </div>
      </ng-template>

      <ng-template #productsContent>
        <div class="card-content products-content">
          <!-- Product Search -->
          <div class="product-search">
            <p-autoComplete
              id="productSearch"
              [(ngModel)]="selectedProduct"
              [ngModelOptions]="{ standalone: true }"
              [suggestions]="productSuggestions()"
              (completeMethod)="productSearch($event)"
              (onSelect)="productSelected($any($event))"
              field="Title"
              [dropdown]="true"
              placeholder="Search products by name or ID..."
              styleClass="w-full"
              appendTo="body"
            >
              <ng-template let-product pTemplate="item">
                <div class="product-suggestion">
                  <span class="product-id">#{{ product.Id }}</span>
                  <span class="product-separator"> | </span>
                  <span class="product-external-id">{{
                    getProductExternalId(product)
                  }}</span>
                  <span class="product-separator"> | </span>
                  <span class="product-name">{{
                    getProductDisplayName(product)
                  }}</span>
                </div>
              </ng-template>
            </p-autoComplete>
            <small class="field-hint">
              Search and select products to add. Variable products are excluded.
            </small>
          </div>

          <!-- Products List -->
          <div class="products-list-container">
            @if (products().length === 0) {
              <div class="empty-products-section">
                <div class="empty-icon">
                  <i class="pi pi-box"></i>
                </div>
                <div class="empty-text">
                  <p class="empty-title">No products added yet</p>
                  <p class="empty-subtitle">
                    Use the search above to add products to this group
                  </p>
                </div>
              </div>
            } @else {
              <div class="products-list">
                @for (product of products(); track product.Id) {
                  <div class="product-row">
                    <div class="product-info">
                      @if (product.ProductId) {
                        <span class="product-item-id"
                          >#{{ product.ProductId }}</span
                        >
                        <span class="product-item-separator"> | </span>
                      }
                      <span class="product-item-external">{{
                        product.Id
                      }}</span>
                      <span class="product-item-separator"> | </span>
                      <span class="product-item-name">{{ product.Title }}</span>
                    </div>
                    <p-button
                      icon="pi pi-trash"
                      [text]="true"
                      severity="danger"
                      size="small"
                      (onClick)="removeProduct(product)"
                      pTooltip="Remove from group"
                    />
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </ng-template>
    </form>
  }
</div>
